define([
    '../Util/defaultValue',
    '../Util/defined',
    '../Util/encode',
    '../Util/callbackSystem',
    '../System/Vec3Obj',
    '../System/ColorObj',
    '../2D/IconSymbol',
    '../2D/TextSymbol',
    '../2D/VectorGraphSymbol'
], function (
    defaultValue,
    defined,
    encode,
    callbackSystem,
    Vec3Obj,
    ColorObj,
    IconSymbol,
    TextSymbol,
    VectorGraphSymbol
) {
    'use strict';

    /**
     * 标注。
     * <br /><br />
     * <div align='center'>
     * <img src='images/Label.png' width='900' height='650' /><br />
     * 标注效果图
     * </div>
     * 
     * @demo {@link http://lbs.zjkelan.com/Label.html|CooWasm Label demo}
     * @alias Label
     * @constructor
     *
     * @param {Object} props 包含如下属性的Object：
     * @param {Vec3Obj} props.location 地理位置坐标值。
     * @param {ColorObj} [props.pointColor=new ColorObj(1.0,1.0,0.0,1.0)] 点颜色。
     * @param {Boolean} [props.bDepthTest=false] 是否开启深度测试。
     * @param {Boolean} [props.bVisible=true] 显隐状态。
     * @param {Number} [props.minAltitude=0.0] 最小可见高度。
     * @param {Number} [props.maxAltitude=3.4028234663852886e+38] 最大可见高度。
     * @param {IconSymbol} props.iconSymbol 图标符号实例。
     * @param {TextSymbol} props.textSymbol 文字符号实例。
     * @param {VectorGraphSymbol} [props.vectorGraphSymbol] 矢量图符号实例。
     * @param {CallbackBoolean} [callback=null] 加载成功后触发的函数回调。
     * @example
     * var label = new CooWasm.Label({
     *     textSymbol: new CooWasm.TextSymbol({
     *              info: "预设标注",
     *              alignMode: CooWasm.EnumAlignmentMode.RIGHT_CENTER
     *     })},(state)=>{console.log("load status:" + state)});
     */
    class Label {
        /**
         * @description 地理位置坐标值。
         * @type {Vec3Obj}
         */
        location
        /**
         * @description 点颜色。
         * @type {ColorObj}
         */
        pointColor
        /**
         * @description 是否开启深度测试。
         * @type {Boolean}
         * @default false
         */
        bDepthTest
        /**
         * @description 标注显隐状态。
         * @type {Boolean}
         * @default true
         */
        bVisible = true
        /**
         * @description 最小可见高度。
         * @type {Number}
         */
        minAltitude
        /**
         * @description 最大可见高度。
         * @type {Number}
         */
        maxAltitude
        /**
        * @description 图标符号实例。
        * @type {IconSymbol}
        */
        iconSymbol
        /**
        * @description 文字符号实例。
        * @type {TextSymbol}
        */
        textSymbol
        /**
         * @description 矢量图符号实例。
         * @type {VectorGraphSymbol}
         */
        vectorGraphSymbol
        /**
         * @description 标注对象uuid。
         * @type {String}
         * @readonly
         */
        avatarUuid = null
        /**
         * @description 标注图标组件uuid。
         * @type {String}
         * @readonly
         */
        componentIconUuid = null
        /**
         * @description 标注文本组件uuid。
         * @type {String}
         * @readonly
         */
        componentTextUuid = null
        /**
         * @description 矢量图组件uuid。
         * @type {String}
         * @readonly
         */
        componentVectorGraphUuid = null
        /**
         * @description 高度控制组件uuid。
         * @type {String}
         * @readonly
         */
        componentAltitudeVisCtrlUuid = null
        /**
         * @description Label在CooWasm中类名。
         * @private
         * @readonly
         */
        className = "Label"

        static avatarType = "deSMP.Avatar.LabelAvatar"
        static componentIconType = "deSMP.AvatarComponent.IconLabelComponent"
        static componentTextType = "deSMP.AvatarComponent.TextLabelComponent"
        static componentVectorGraphType = "AvatarComponent.LabelComponent.VectorGraphicLabelComponent"
        static componentAltitudeVisCtrlType = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"

        constructor(props, callback) {
            if (!defined(props)) {
                props = {}
            }
            if (defined(props.build) && props.build == true) {
                this.textSymbol = new TextSymbol({})
                this.iconSymbol = new IconSymbol({})
                this.vectorGraphSymbol = new VectorGraphSymbol({})
                return
            }
            if (!defined(props.iconSymbol) && !defined(props.textSymbol) && !defined(props.vectorGraphSymbol)) {
                console.log("iconSymbol and textSymbol and vectorGraphSymbol are all null!")
                return null
            }

            if (defined(props.iconSymbol)) {
                this.iconSymbol = props.iconSymbol
            }
            if (defined(props.textSymbol)) {
                this.textSymbol = props.textSymbol
            }
            if (defined(props.vectorGraphSymbol)) {
                this.vectorGraphSymbol = props.vectorGraphSymbol
            }
            //create avatars
            let avatarCategoryName = "deSMP.Avatar.LabelAvatar"
            let iconCategoryName = "deSMP.AvatarComponent.IconLabelComponent"
            let textCategoryName = "deSMP.AvatarComponent.TextLabelComponent"
            let vectorGraphCategoryName = "AvatarComponent.LabelComponent.VectorGraphicLabelComponent"
            let avcCategoryName = "deSMP.AvatarComponent.AltitudeVisCtrlComponent"
            let categoryNames = ""
            let avatarProps = ""
            let componetProps = ""
            //avatar props
            avatarProps += this.toTuple3Str("location", props.location, "Point")
            avatarProps += this.toStr("pointColor", props.pointColor, "PointColor")
            avatarProps += this.toStr("bDepthTest", props.bDepthTest, "DepthTest")
            avatarProps += this.toStr("bVisible", props.bVisible, "Visible")
            //icon componet props
            let bIcon = defined(props.iconSymbol)
            let bText = defined(props.textSymbol)
            let bVectorGraph = defined(props.vectorGraphSymbol)
            if (bIcon) {
                this.iconSymbol = props.iconSymbol
                let iconProps = props.iconSymbol.properties
                componetProps += iconProps + "#$%"
                categoryNames += iconCategoryName + "#$%"
            }
            //text componet props
            if (bText) {
                this.textSymbol = props.textSymbol
                let textProps = props.textSymbol.properties
                componetProps += textProps + "#$%"
                categoryNames += textCategoryName + "#$%"
            }
            //vectorGraph componet props
            if (bVectorGraph) {
                this.vectorGraphSymbol = props.vectorGraphSymbol
                let vectorGraphProps = props.vectorGraphSymbol.properties
                componetProps += vectorGraphProps + "#$%"
                categoryNames += vectorGraphCategoryName + "#$%"
            }
            //altitudeVisCtrl componet props
            let avcComponentProps = ""
            avcComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
            avcComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")
            componetProps += avcComponentProps + "#$%"
            categoryNames += avcCategoryName + "#$%"
            var idInfo = ""
            if (defined(this.location)) {//如果有传入点
                idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(categoryNames), encode(componetProps), false, this.bVisible, defined(callback))
            } else {
                idInfo = Element.prototype.NativeAPI.LoadAvatarAndMultiComponent(encode(avatarCategoryName), encode(avatarProps), encode(categoryNames), encode(componetProps), true, this.bVisible, false)
            }
            if (idInfo != "") {
                var idArray = idInfo.split('#$%')
                this.avatarUuid = idArray[0]
                if (bIcon && bText && bVectorGraph) {
                    this.componentIconUuid = idArray[1]
                    this.componentTextUuid = idArray[2]
                    this.componentVectorGraphUuid = idArray[3]
                    this.componentAltitudeVisCtrlUuid = idArray[4]
                } else if (!bIcon && bText && bVectorGraph) {
                    this.componentTextUuid = idArray[1]
                    this.componentVectorGraphUuid = idArray[2]
                    this.componentAltitudeVisCtrlUuid = idArray[3]
                } else if (bIcon && !bText && bVectorGraph) {
                    this.componentIconUuid = idArray[1]
                    this.componentVectorGraphUuid = idArray[2]
                    this.componentAltitudeVisCtrlUuid = idArray[3]
                } else if (bIcon && bText && !bVectorGraph) {
                    this.componentIconUuid = idArray[1]
                    this.componentTextUuid = idArray[2]
                    this.componentAltitudeVisCtrlUuid = idArray[3]
                }
                else if (!bIcon && !bText && bVectorGraph) {
                    this.componentVectorGraphUuid = idArray[1]
                    this.componentAltitudeVisCtrlUuid = idArray[2]
                } else if (bIcon && !bText && !bVectorGraph) {
                    this.componentIconUuid = idArray[1]
                    this.componentAltitudeVisCtrlUuid = idArray[2]
                } else if (!bIcon && bText && !bVectorGraph) {
                    this.componentTextUuid = idArray[1]
                    this.componentAltitudeVisCtrlUuid = idArray[2]
                }
                callbackSystem.register("LoadAvatarAndComponent", this.avatarUuid, (state) => {
                    if (defined(callback)) {
                        callback(state)
                    }
                    Element.prototype.Map.labels.push(this)
                })
            }
        }

        toStr(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                if (typeof value.toStr === 'function') {
                    result = key + ":" + value.toStr() + "|"
                } else {
                    result = key + ":" + value + "|"
                }
            }
            return result
        }
        toTuple3Str(memName, value, key) {
            let result = ""
            if (defined(value)) {
                this[memName] = value
                const posStr = `{"x":${value.x},"y":${value.y},"z":${value.z}}`
                result = key + ":" + posStr + "|"
            }
            return result
        }
        /**
         * <p>
         * 更新单个或者多个对象属性，使之立即生效。
         * </p>
         *
         * @param {Object} [props] 包含如下属性的Object。
         * @param {Vec3Obj} [props.location] 地理位置坐标值。
         * @param {ColorObj} [props.pointColor] 点颜色。
         * @param {Boolean} [props.bDepthTest] 是否开启深度测试。
         * @param {Boolean} [props.bVisible] 显隐状态。
         * @param {Number} [props.minAltitude] 最小可见高度。
         * @param {Number} [props.maxAltitude] 最大可见高度。
         * @param {IconSymbol} [props.iconSymbol] 图标符号实例。
         * @param {TextSymbol} [props.textSymbol] 文字符号实例。
         * @param {VectorGraphSymbol} [props.vectorGraphSymbol] 矢量图符号实例。
         * @param {CallbackBoolean} [callback] 更新成功后触发的函数回调。
         * @example
         * label.update({
         *    bVisible:false
         * },(state)=>{console.log("update status:" + state)});
         */
        update(props, callback) {
            let avatarProps = ""
            let componentProps = ""
            let uuids = ""
            if (defined(props)) {
                if (defined(props.location)) {
                    //this.location = props.location
                    avatarProps += this.toTuple3Str("location", props.location, "Point")
                }
                if (defined(props.pointColor)) {
                    this.pointColor = props.pointColor
                    avatarProps += "PointColor:" + this.pointColor.toStr() + "|"
                }
                if (defined(props.bDepthTest)) {
                    this.bDepthTest = props.bDepthTest
                    avatarProps += "DepthTest:" + this.bDepthTest + "|"
                }
                if (defined(props.bVisible)) {
                    this.bVisible = props.bVisible
                    avatarProps += "Visible:" + this.bVisible + "|"
                }
                //textSymbol
                if (defined(props.textSymbol)) {
                    componentProps += this.textSymbol.update(props.textSymbol) + "#$%"
                    uuids += this.componentTextUuid + "#$%"
                }
                //iconSymbol
                if (defined(props.iconSymbol)) {
                    componentProps += this.iconSymbol.update(props.iconSymbol) + "#$%"
                    uuids += this.componentIconUuid + "#$%"
                }
                //vectorGraphSymbol
                if (defined(props.vectorGraphSymbol)) {
                    componentProps += this.vectorGraphSymbol.update(props.vectorGraphSymbol) + "#$%"
                    uuids += this.componentVectorGraphUuid + "#$%"
                }
                //altitudeVisCtrl
                let altitudeVisCtrlComponentProps = ""
                altitudeVisCtrlComponentProps += this.toStr("minAltitude", props.minAltitude, "MinVisibleAltitude")
                altitudeVisCtrlComponentProps += this.toStr("maxAltitude", props.maxAltitude, "MaxVisibleAltitude")

                componentProps += altitudeVisCtrlComponentProps + "#$%"
                uuids += this.componentAltitudeVisCtrlUuid + "#$%"

                callbackSystem.register("UpdateAvatarAndMultiComponent", this.avatarUuid, (state) => {
                    this.getProperties()
                    if (defined(callback)) {
                        callback(state)
                    }
                })
                Element.prototype.NativeAPI.UpdateAvatarAndMultiComponent(encode(this.avatarUuid), encode(uuids), encode(avatarProps), encode(componentProps), defined(callback))
            }
        }

        /**
         * 从场景中定位自身(自动根据数据包围盒定位，不可修改定位参数)。
         * @param {CallbackBoolean} [callback=null] 飞行结束后触发的函数回调。
         * @example
         * label.locate((state)=>{console.log("locate status:" + state)});
         */
        locate(callback) {
            callbackSystem.register("", "LocateAvatar", (state) => {
                if (defined(callback)) {
                    callback(state)
                }
            })
            Element.prototype.NativeAPI.LocateAvatar(encode(this.avatarUuid))
        }

        /**
         * 获取对象包含的属性。
         * @returns {Boolean} 是否获取成功。获取成功时，会自动赋值给属性。
         * @example
         * label.getProperties();
         */
        getProperties() {
            if (!defined(this.avatarUuid)) {
                console.log("uuid require!")
                return false
            }
            let pAvatarUuid = Element.prototype.NativeAPI.Uuid_New()
            Element.prototype.NativeAPI.Uuid_SetId(pAvatarUuid, encode(this.avatarUuid))
            let pAvatar = Element.prototype.NativeAPI.MapImpl_getAvatar(pAvatarUuid)
            let avatarPropSize = Element.prototype.NativeAPI.Avatar_GetPropertiesSize(pAvatar)
            for (let i = 0; i < avatarPropSize; ++i) {
                let pProp = Element.prototype.NativeAPI.Avatar_GetProperty(pAvatar, i)
                let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                if (name == "Visible") {
                    if (value == "true" || value == "1") {
                        this.bVisible = true
                    } else {
                        this.bVisible = false
                    }
                } else if (name == "DepthTest") {
                    if (value == "true" || value == "1") {
                        this.bDepthTest = true
                    } else {
                        this.bDepthTest = false
                    }
                } else if (name == "PointColor") {
                    this.pointColor = ColorObj.fromStr(value)
                } else if (name == "Point") {
                    this.location = Vec3Obj.fromStr(value)
                }
            }
            let avatarAcSize = Element.prototype.NativeAPI.Avatar_GetAvatarComponentsSize(pAvatar)
            for (let j = 0; j < avatarAcSize; ++j) {
                let pAc = Element.prototype.NativeAPI.Avatar_GetAvatarComponent(pAvatar, j)
                let propSize = Element.prototype.NativeAPI.AvatarComponent_GetPropertiesSize(pAc)
                let pObjType = Element.prototype.NativeAPI.AvatarComponent_GetObjectType(pAc)
                let pName = Element.prototype.NativeAPI.ObjectType_GetName(pObjType)
                let pUuid = Element.prototype.NativeAPI.AvatarComponent_GetUuid(pAc)
                let uuid = Element.prototype.NativeAPI.Uuid_GetId(pUuid)
                if (pName == "IconLabelComponent") {
                    this.componentIconUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "AlignmentMode") {
                            this.iconSymbol.alignMode = Number(value)
                        } else if (name == "RotateMode") {
                            this.iconSymbol.rotateMode = Number(value)
                        } else if (name == "ZoomMode") {
                            this.iconSymbol.zoomMode = Number(value)
                        } else if (name == "ZoomMaxDistance") {
                            this.iconSymbol.zoomLimit = Number(value)
                        } else if (name == "MinVisibleDistance") {
                            this.iconSymbol.minDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.iconSymbol.maxDistance = Number(value)
                        } else if (name == "LiftUp") {
                            this.iconSymbol.liftUp = Number(value)
                        } else if (name == "Url") {
                            this.iconSymbol.url = value
                        } else if (name == "Scale") {
                            this.iconSymbol.scale = Number(value)
                        }
                    }
                } else if (pName == "TextLabelComponent") {
                    this.componentTextUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "AlignmentMode") {
                            this.textSymbol.alignMode = Number(value)
                        } else if (name == "RotateMode") {
                            this.textSymbol.rotateMode = Number(value)
                        } else if (name == "ZoomMode") {
                            this.textSymbol.zoomMode = Number(value)
                        } else if (name == "ZoomMaxDistance") {
                            this.textSymbol.zoomLimit = Number(value)
                        } else if (name == "MinVisibleDistance") {
                            this.textSymbol.minDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.textSymbol.maxDistance = Number(value)
                        } else if (name == "LiftUp") {
                            this.textSymbol.liftUp = Number(value)
                        } else if (name == "Info") {
                            this.textSymbol.info = value
                        } else if (name == "Scale") {
                            this.textSymbol.scale = Number(value)
                        } else if (name == "FontUrl") {
                            this.textSymbol.fontUrl = value
                        } else if (name == "Size") {
                            this.textSymbol.size = Number(value)
                        } else if (name == "Color") {
                            this.textSymbol.color = ColorObj.fromStr(value)
                        } else if (name == "Outline") {
                            if (value == "true" || value == "1") {
                                this.textSymbol.bOutline = true
                            } else {
                                this.textSymbol.bOutline = false
                            }
                        } else if (name == "OutlineColor") {
                            this.textSymbol.outlineColor = ColorObj.fromStr(value)
                        } else if (name == "OutlineWidth") {
                            this.textSymbol.outlineWidth = Number(value)
                        } else if (name == "Embolden") {
                            if (value == "true" || value == "1") {
                                this.textSymbol.bEmbolden = true
                            } else {
                                this.textSymbol.bEmbolden = false
                            }
                        } else if (name == "Italics") {
                            if (value == "true" || value == "1") {
                                this.textSymbol.bItalics = true
                            } else {
                                this.textSymbol.bItalics = false
                            }
                        } else if (name == "Underline") {
                            if (value == "true" || value == "1") {
                                this.textSymbol.bUnderline = true
                            } else {
                                this.textSymbol.bUnderline = false
                            }
                        } else if (name == "UnderlineColor") {
                            this.textSymbol.underlineColor = ColorObj.fromStr(value)
                        } else if (name == "Horizontal") {
                            if (value == "true" || value == "1") {
                                this.textSymbol.bHorizontal = true
                            } else {
                                this.textSymbol.bHorizontal = false
                            }
                        } else if (name == "LetterSpacing") {
                            this.textSymbol.letterSpacing = Number(value)
                        } else if (name == "BackdropMode") {
                            this.textSymbol.backdropMode = Number(value)
                        } else if (name == "BackdropColor") {
                            this.textSymbol.backdropColor = ColorObj.fromStr(value)
                        } else if (name == "BackdropImageUrl") {
                            this.textSymbol.backdropUrl = value
                        } else if (name == "BackdropMarginTop") {
                            this.textSymbol.backdropMarginTop = Number(value)
                        } else if (name == "BackdropMarginBottom") {
                            this.textSymbol.backdropMarginBottom = Number(value)
                        } else if (name == "BackdropMarginLeft") {
                            this.textSymbol.backdropMarginLeft = Number(value)
                        } else if (name == "BackdropMarginRight") {
                            this.textSymbol.backdropMarginRight = Number(value)
                        }
                    }
                } else if (pName == "VectorGraphicLabelComponent") {
                    this.componentVectorGraphUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "AlignmentMode") {
                            this.vectorGraphSymbol.alignMode = Number(value)
                        } else if (name == "RotateMode") {
                            this.vectorGraphSymbol.rotateMode = Number(value)
                        } else if (name == "ZoomMode") {
                            this.vectorGraphSymbol.zoomMode = Number(value)
                        } else if (name == "ZoomMaxDistance") {
                            this.vectorGraphSymbol.zoomLimit = Number(value)
                        } else if (name == "MinVisibleDistance") {
                            this.vectorGraphSymbol.minDistance = Number(value)
                        } else if (name == "MaxVisibleDistance") {
                            this.vectorGraphSymbol.maxDistance = Number(value)
                        } else if (name == "LiftUp") {
                            this.vectorGraphSymbol.liftUp = Number(value)
                        } else if (name == "VectorGraphicInfo") {
                            this.vectorGraphSymbol.info = value
                        } else if (name == "ColorMode") {
                            this.vectorGraphSymbol.colorMode = value
                        } else if (name == "Color") {
                            this.vectorGraphSymbol.color = CooWasm.ColorObj.fromCssColorString(value)
                        } else if (name == "Scale") {
                            this.vectorGraphSymbol.scale = Number(value)
                        }
                    }
                } else if (pName == "AltitudeVisCtrlComponent") {
                    this.componentAltitudeVisCtrlUuid = uuid
                    for (let z = 0; z < propSize; ++z) {
                        let pProp = Element.prototype.NativeAPI.AvatarComponent_GetProperty(pAc, z)
                        let name = Element.prototype.NativeAPI.Property_GetName(pProp)
                        let value = Element.prototype.NativeAPI.Property_GetValue(pProp)
                        if (name == "MinVisibleAltitude") {
                            this.minAltitude = Number(value)
                        } else if (name == "MaxVisibleAltitude") {
                            this.maxAltitude = Number(value)
                        }
                    }
                }
            }
            Element.prototype.NativeAPI.Avatar_Delete(pAvatar)
            Element.prototype.NativeAPI.Uuid_Delete(pAvatarUuid)
            return true
        }
        /**
         * 从场景中销毁自身。
         * @param {CallbackBoolean} [callback=null] 销毁后触发的函数回调。
         * @returns null 空值。
         * @example
         * label = label.destroy((state)=>{console.log("destroy status:" + state)});
         */
        destroy(callback) {
            if (!defined(this.avatarUuid)) {
                console.log("avatarUuid is null!")
                return null
            }
            callbackSystem.register("RemoveAvatar", this.avatarUuid, (state) => {
                if (defined(callback)) {
                    callback(state)
                }
                Element.prototype.Map.labels = Element.prototype.Map.labels.filter(item => item !== this)
                callbackSystem.remove("LoadAvatarAndComponent", this.avatarUuid)
                callbackSystem.remove("UpdateAvatarAndMultiComponent", this.avatarUuid)
                callbackSystem.remove("", "LocateAvatar")
                callbackSystem.remove("RemoveAvatar", this.avatarUuid)
                this.avatarUuid = null
                this.componentIconUuid = null
                this.componentTextUuid = null
                this.componentVectorGraphUuid = null
                this.componentAltitudeVisCtrlUuid = null
            })
            Element.prototype.NativeAPI.RemoveAvatar(encode(this.avatarUuid))
            return null
        }
    }
    return Label
})